import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, onMounted, ref, watch } from "vue";
import Multiselect from "vue-multiselect";
import { useFilterObjectArray } from "@/composables/filter";
import { useMultiselect } from "@/composables/useMultiselect";
import { uid } from "@/utils/utils";
import { isDataOption, itemUniqueKey } from "./FormData/types";
import StatelessTags from "@/components/TagsMultiselect/StatelessTags.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FormSelect',
    props: {
        id: { type: String, default: `form-select-${uid()}` },
        disabled: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        optional: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: "Select Value",
        },
        value: {
            type: [String, Array],
            default: null,
        },
    },
    emits: ["input"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faCheckSquare, faSquare);
        const { ariaExpanded, onOpen, onClose } = useMultiselect();
        const filter = ref("");
        const filteredOptions = useFilterObjectArray(() => props.options, filter, ["label", ["value", "tags"]]);
        /**
         * When there are more options than this, push selected options to the end
         */
        const optionReorderThreshold = 8;
        const reorderedOptions = computed(() => {
            let result;
            if (!props.multiple || filteredOptions.value.length <= optionReorderThreshold) {
                result = filteredOptions.value;
            }
            else {
                const selectedOptions = [];
                const unselectedOptions = [];
                filteredOptions.value.forEach((option) => {
                    if (isSelected(option.value)) {
                        selectedOptions.push(option);
                    }
                    else {
                        unselectedOptions.push(option);
                    }
                });
                result = [...unselectedOptions, ...selectedOptions];
            }
            return result.map(getSelectOption);
        });
        /**
         * Tracks if the select field has options
         */
        const hasOptions = computed(() => {
            return props.options.length > 0;
        });
        /**
         * Provides initial value if necessary
         */
        const initialValue = computed(() => {
            if (props.value === null && !props.optional && hasOptions.value) {
                const v = props.options[0];
                if (v) {
                    return v.value;
                }
            }
            return null;
        });
        /**
         * Configure selected label
         */
        const selectedLabel = computed(() => {
            return props.multiple ? "Selected" : "";
        });
        /**
         * Tracks selected values
         */
        const selectedValues = computed(() => (Array.isArray(props.value) ? props.value : [props.value]));
        /**
         * Tracks selected keys in case of form data options
         */
        const selectedKeys = computed(() => {
            return selectedValues.value
                .map((v) => (isDataOptionObject(v) ? itemUniqueKey(v) : undefined))
                .filter((v) => v !== undefined);
        });
        /**
         * Whether current value(s) will be tracked by key or value
         */
        const trackBy = computed(() => {
            return selectedKeys.value.length > 0 ? "key" : "value";
        });
        /**
         * Tracks current value and emits changes
         */
        const currentValue = computed({
            get: () => props.options.filter((option) => isSelected(option.value)).map(getSelectOption),
            set: (val) => {
                if (Array.isArray(val)) {
                    if (val.length > 0) {
                        const values = val.map((v) => v.value);
                        emit("input", values);
                    }
                    else {
                        emit("input", null);
                    }
                }
                else {
                    emit("input", val ? val.value : null);
                }
            },
        });
        /**
         * Ensures that an initial value is selected for non-optional inputs
         */
        function setInitialValue() {
            if (initialValue.value) {
                emit("input", initialValue.value);
            }
        }
        function getSelectOption(option) {
            if (isDataOptionObject(option.value)) {
                return {
                    ...option,
                    key: itemUniqueKey(option.value),
                };
            }
            return option;
        }
        /**
         * Watches changes in select options and adjusts initial value if necessary
         */
        watch(() => props.options, () => {
            setInitialValue();
        });
        /**
         * Sets initial value if necessary
         */
        onMounted(() => {
            setInitialValue();
        });
        function isValueWithTags(item) {
            return item !== null && typeof item === "object" && item.tags !== undefined;
        }
        function isDataOptionObject(item) {
            return !!item && typeof item === "object" && isDataOption(item);
        }
        function onSearchChange(search) {
            filter.value = search;
        }
        function isSelected(item) {
            if (isDataOptionObject(item)) {
                return selectedKeys.value.includes(itemUniqueKey(item));
            }
            return selectedValues.value.includes(item);
        }
        return { __sfc: true, ariaExpanded, onOpen, onClose, props, emit, filter, filteredOptions, optionReorderThreshold, reorderedOptions, hasOptions, initialValue, selectedLabel, selectedValues, selectedKeys, trackBy, currentValue, setInitialValue, getSelectOption, isValueWithTags, isDataOptionObject, onSearchChange, isSelected, faCheckSquare, faSquare, FontAwesomeIcon, Multiselect, StatelessTags };
    }
});
